import type { PositionDto } from '@et/shared';
import { Decimal } from 'decimal.js';

/**
 * What an open position is worth right now, with both commissions accounted for. Computed in
 * the browser because `lastPrice` moves with every WS tick while the server-side fields
 * (cost, fees paid, fee rate) do not. Decimal all the way (AI_RULES §5); strings out.
 */
export interface PositionFigures {
  /** Cost of the held quantity plus the commission already paid. */
  readonly totalCost: string;
  /** `qty × lastPrice`, before the exit commission. */
  readonly currentValue: string | null;
  /** Commission a market sell would pay now. */
  readonly exitFee: string | null;
  /** What selling everything now would put in the pocket, after both commissions. */
  readonly netPnl: string | null;
  readonly netPnlPct: string | null;
}

export function positionFigures(p: PositionDto): PositionFigures {
  const entryValue = new Decimal(p.entryValue);
  const totalCost = entryValue.plus(p.feesPaid);
  if (p.lastPrice === null) {
    return { totalCost: totalCost.toFixed(4), currentValue: null, exitFee: null, netPnl: null, netPnlPct: null };
  }
  const currentValue = new Decimal(p.qty).times(p.lastPrice);
  const exitFee = currentValue.times(p.exitFeeRate);
  const netPnl = currentValue.minus(exitFee).minus(totalCost);
  const netPnlPct = totalCost.isZero() ? null : netPnl.dividedBy(totalCost).times(100);
  return {
    totalCost: totalCost.toFixed(4),
    currentValue: currentValue.toFixed(4),
    exitFee: exitFee.toFixed(4),
    netPnl: netPnl.toFixed(4),
    netPnlPct: netPnlPct === null ? null : netPnlPct.toFixed(2),
  };
}

/** "SOL/USDT" → "BINANCE:SOLUSDT" for a TradingView link. */
export function tradingViewSymbol(symbol: string): string {
  return `BINANCE:${symbol.replace('/', '')}`;
}
